import { Text, TextInput } from "@ucl/react-library";
import { useRef, type ChangeEvent } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useBoundInput } from "src/hooks/use-bound-input";
import { ImportantInformation } from "../important-information/important-information";
import { PayingAnotherWay } from "../paying-another-way/paying-another-way";

type BankDetailsFormValues = {
  bankDetails: {
    payeeName: string;
    sortCode: string;
    accountNumber: string;
  };
  agreeTsAndCs: number;
};

const formatSortCode = (digits: string, addTrailingHyphen: boolean) => {
  const value = digits.replace(/\D/g, "").slice(0, 6);

  if (value.length <= 2) {
    return value.length === 2 && addTrailingHyphen ? `${value}-` : value;
  }

  if (value.length <= 4) {
    return `${value.slice(0, 2)}-${value.slice(2)}${value.length === 4 && addTrailingHyphen ? "-" : ""}`;
  }

  return `${value.slice(0, 2)}-${value.slice(2, 4)}-${value.slice(4, 6)}`;
};

const formatAccountNumber = (value: string) => {
  return value.replace(/\D/g, "").slice(0, 8);
};

export const BankDetailsForm = () => {
  const previousSortCodeDigitsRef = useRef("");

  const { t } = useTranslation();

  const {
    control,
    setValue,
  } = useFormContext<BankDetailsFormValues>();

  const payeeNameProps = useBoundInput({
    name: "bankDetails.payeeName",
    control,
  });

  const sortCodeProps = useBoundInput({
    name: "bankDetails.sortCode",
    control,
  });

  const accountNumberProps = useBoundInput({
    name: "bankDetails.accountNumber",
    control,
  });

  const handleSortCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextDigits = event.target.value.replace(/\D/g, "").slice(0, 6);
    const previousDigits = previousSortCodeDigitsRef.current;

    const isAdding = nextDigits.length > previousDigits.length;

    const formattedValue = formatSortCode(nextDigits, isAdding);

    previousSortCodeDigitsRef.current = nextDigits;

    setValue("bankDetails.sortCode", formattedValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: sortCodeProps.inputState === "error"

    });
  };

  const handleAccountNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatAccountNumber(event.target.value);

    setValue("bankDetails.accountNumber", formattedValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: accountNumberProps.inputState === "error"
    });
  };

  return (
    <div data-testid="bankDetailsForm" className="flex w-full max-w-[640px] flex-col gap-8">
      <div className="flex flex-col gap-3">
        <Text.H4 fontWeight="semi-bold">{t("bankDetailsForm.title")}</Text.H4>
        <Text.P size="large">{t("bankDetailsForm.subtitle")}</Text.P>
      </div>

      <div className="flex flex-col gap-5">
        <div>
          <TextInput
            data-testid="payeeName"
            labelProp={{ label: t("bankDetailsForm.payeeName") }}
            placeholder=""
            {...payeeNameProps}
          />
        </div>

        <div className="flex flex-col gap-2">
          <TextInput
            data-testid="sortCode"
            labelProp={{ label: t("bankDetailsForm.sortCode") }}
            placeholder=""
            inputMode="numeric"
            maxLength={8}
            {...sortCodeProps}
            onChange={handleSortCodeChange}
          />
        </div>

        <div className="flex flex-col gap-2">
          <TextInput
            data-testid="accountNumber"
            labelProp={{ label: t("bankDetailsForm.accountNumber") }}
            placeholder=""
            inputMode="numeric"
            maxLength={8}
            {...accountNumberProps}
            onChange={handleAccountNumberChange}
          />
        </div>
      </div>

      <PayingAnotherWay />
      <div className="flex flex-col gap-5">
        <Text renderAs="h3" styleType="h4" fontWeight="semi-bold">
          {t("importantInformation.title")}
        </Text>
        <ImportantInformation showOptInInfo={false} />
      </div>
    </div>
  );
};
